/* eslint-disable max-lines */
import { useState, SyntheticEvent } from 'react';
import styled, { css } from 'styled-components';
import { Collapse } from 'reactstrap';
import i18n from 'src/utils/translate';
import { ExtendedMember } from 'src/constants/types';
import MemberCardTags from 'src/components/memberCardTags';
import Avatar, { Size } from 'src/components/avatar';
import Badge from 'src/components/badge';
import { Card } from 'src/modules/home/explore/cards/sharedStyles';
import { titleSmall, text, textLinkPrimary } from 'styles-js/mixins/typography';
import { fadeEllipsis } from 'styles-js/mixins/fade';
import Button from 'src/components/buttons/button';
import { LinkButton } from 'styles-js/buttons';
import FollowButton from 'src/components/buttons/followButton/followButton';
import { Section, Element } from 'src/constants/footfall';
import { useSelector } from 'src/store/store';
import MemberCardInfo from 'src/components/memberCardInfo';
type MemberCardProps = {
  member: ExtendedMember;
  isExpandable?: boolean;
  isFollowButton?: boolean;
  isLink?: boolean;
  isAuthorEmptyProfile?: boolean;
  isUserLoggedIn?: boolean;
  onClick?: (href: string, username: string) => void;
  onDropdownClick?: () => void;
  onFollowClick?: () => void;
  onLinkClick?: (type?: string) => void;
};
export default function MemberCard({
  member: {
    age,
    bio,
    country,
    gender,
    healthInterests,
    profileImageHash = '',
    profileImageSmall2x,
    roles,
    username,
    isFollowedByCurrentUser,
    isUserFollowsEnabled,
    userId
  },
  isAuthorEmptyProfile = false,
  isExpandable = false,
  isFollowButton = false,
  isLink = false,
  isUserLoggedIn = true,
  onClick,
  onDropdownClick,
  onFollowClick,
  onLinkClick
}: MemberCardProps) {
  const currentUser = useSelector(({
    context
  }) => context.currentUser);
  const [isOpen, setIsOpen] = useState(!isExpandable);
  const parsedBio = bio?.replace(/[\b\t\n\f\r]/g, ' ')?.trim();
  const toggleCardExpand = () => {
    setIsOpen(!isOpen);
    onDropdownClick?.();
  };
  return <Container $isLink={isLink} as={isLink ? 'a' : 'div'} href={isLink ? `/user/${username}` : '#'} onClick={() => isLink ? onClick?.(`/user/${username}`, username) : null} data-sentry-element="Container" data-sentry-component="MemberCard" data-sentry-source-file="memberCard.tsx">
      <StyledCard $isExpandable={isExpandable || isAuthorEmptyProfile || !isUserLoggedIn} $isFollowButton={isFollowButton} data-sentry-element="StyledCard" data-sentry-source-file="memberCard.tsx">
        <User data-sentry-element="User" data-sentry-source-file="memberCard.tsx">
          <Link $isLink={isUserLoggedIn && !isLink} as={isLink || !isUserLoggedIn ? 'div' : 'a'} href={isLink || !isUserLoggedIn ? '#' : `/user/${username}`} onClick={() => isUserLoggedIn ? onClick?.(`/user/${username}`, username) : null} data-sentry-element="Link" data-sentry-source-file="memberCard.tsx">
            <AvatarWrapper data-sentry-element="AvatarWrapper" data-sentry-source-file="memberCard.tsx">
              {profileImageSmall2x ? <Avatar name={username} size={Size.Medium} url={profileImageSmall2x} /> : <Avatar hash={profileImageHash} name={username} size={Size.Medium} />}
            </AvatarWrapper>
            <UsernameWrapper data-sentry-element="UsernameWrapper" data-sentry-source-file="memberCard.tsx">
              <Username data-testid="member-card-username" data-sentry-element="Username" data-sentry-source-file="memberCard.tsx">{username}</Username>
              {roles && roles?.length > 0 && <div>
                  <StyledBadge>{roles[0]}</StyledBadge>
                </div>}
            </UsernameWrapper>
          </Link>
          {isExpandable && (parsedBio?.length > 0 || healthInterests?.length > 0) && <ToggleButton ariaLabel={`View more info on ${username}`} icon={isOpen ? 'open-up' : 'open-down'} type="link" onClick={toggleCardExpand} />}
        </User>
        {isUserLoggedIn && !isAuthorEmptyProfile && (!isExpandable || gender || age || country) && <MemberCardInfo age={age} country={country} gender={gender} />}
        {!isUserLoggedIn || isAuthorEmptyProfile ? <Collapse isOpen={isOpen}>
            {!isUserLoggedIn ? <Message>{i18n.t('To view profiles and participate in discussions please ')}
                <LinkButton onClick={() => onLinkClick?.('join')}>{i18n.t('join')}</LinkButton>
                {i18n.t(' or ')}
                <LinkButton onClick={() => onLinkClick?.('login')}>{i18n.t('log in')}</LinkButton>.
              </Message> : <Message>{i18n.t('Help members answer you by giving more ')}
                <LinkSpan onClick={() => onLinkClick?.()}>{i18n.t('context on your profile')}</LinkSpan>
              </Message>}
          </Collapse> : <Collapse isOpen={isOpen}>
            {(isExpandable && parsedBio?.length > 0 || !isExpandable) && <Bio>{parsedBio}</Bio>}
            {(healthInterests?.length > 0 || isFollowButton) && <MemberCardTags hasLink={isLink || isExpandable} tags={healthInterests} username={username} onMoreClick={onClick} />}
          </Collapse>}
        {!!currentUser && isFollowButton && userId && <StyledFollowButton buttonType={isFollowedByCurrentUser ? 'secondary' : 'primary'} clickMetadata={{
        element: Element.Follow,
        section: Section.PublicProfile
      }} followText={isFollowedByCurrentUser ? 'Following' : 'Follow'} followType="user" hasIcon={isFollowedByCurrentUser} isDisabled={currentUser.userId === userId || !isUserFollowsEnabled} isFollowing={isFollowedByCurrentUser} userId={userId} username={username} onFollowClick={onFollowClick} />}
      </StyledCard>
    </Container>;
}
type LinkProps = {
  $isLink?: boolean;
  as?: React.ElementType;
  href?: string;
  onClick: (event: SyntheticEvent) => void;
};
const Container = styled.a<LinkProps>`
  display: block;
  ${({
  $isLink
}) => $isLink && css`
    &:hover ${Username}, &:focus ${Username}, &:active ${Username} {
      color: ${({
  theme
}) => theme.colorGreyDark};
    }
  `}
`;
const Link = styled.a<LinkProps>`
  display: flex;
  flex-grow: 2;
  overflow: hidden;
  ${({
  $isLink
}) => $isLink && css`
    &:hover ${Username}, &:focus ${Username}, &:active ${Username} {
      color: ${({
  theme
}) => theme.colorGreyDark};
    }
  `}
`;
const LinkSpan = styled.span<LinkProps>`
  ${textLinkPrimary};
`;
const StyledCard = styled(Card)<{
  $isExpandable?: boolean;
  $isFollowButton?: boolean;
}>`
  height: ${props => !props.$isExpandable ? props.$isFollowButton ? '270px' : '235px' : 'auto'};
  max-width: ${props => !props.$isExpandable ? '366px' : 'auto'};
  margin: ${props => !props.$isExpandable ? '14px 3px' : '14px 0'};
  overflow: hidden;
  @media (${({
  theme
}) => theme.underScreenSmall}) {
    height: ${props => !props.$isExpandable ? props.$isFollowButton ? '268px' : '235px' : 'auto'};
  }
`;
const User = styled.div`
  display: flex;
  height: 40px;
  align-items: center;
  margin-bottom: 14px;
`;
const AvatarWrapper = styled.div`
  img {
    width: 40px;
    height: 40px;
    margin-right: 10px;
  }
`;
const Username = styled.span`
  ${titleSmall};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.5s ease;
`;
const UsernameWrapper = styled.div`
  overflow: hidden;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;
export const Bio = styled.p`
  ${text}
  height: calc(${({
  theme
}) => theme.fontLineHeightS} * 2);
  position: relative;
  overflow: hidden;
  margin-bottom: 14px;
  ::after {
    ${fadeEllipsis};
  }
`;
const Message = styled.div`
  position: relative;
  overflow: hidden;
  color: ${({
  theme
}) => theme.colorTextLight};
`;
const ToggleButton = styled(Button)`
  padding: 6px 12px;
  margin: 0;
`;
const StyledFollowButton = styled(FollowButton)`
  margin: 5px 0 0;
`;
const StyledBadge = styled(Badge)`
  display: inline-block;
  margin-top: 4px;
`;